import React from 'react';
import { X, Loader2, AlertCircle, User, Building, FileText, ExternalLink } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return new Date(dateStr.split('T')[0] + 'T00:00:00').toLocaleDateString('pt-BR');
  } catch { return dateStr; }
};

export default function ModalEnriquecimento({ isOpen, onClose, envolvido, dados, loading, error, darkMode }) {
  if (!isOpen) return null;

  const envolvido_encontrado = dados?.envolvido_encontrado;
  const items = dados?.items || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div className={`relative w-full max-w-3xl max-h-[90vh] overflow-hidden rounded-xl shadow-2xl flex flex-col ${
        darkMode ? 'bg-neutral-900 border border-neutral-800' : 'bg-white border border-gray-200'
      }`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-6 border-b flex-shrink-0 ${
          darkMode ? 'border-neutral-800 bg-neutral-800/50' : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {envolvido_encontrado?.tipo_pessoa === 'JURIDICA'
                ? <Building className={`w-5 h-5 ${darkMode ? 'text-primary-400' : 'text-primary-600'}`} />
                : <User className={`w-5 h-5 ${darkMode ? 'text-primary-400' : 'text-primary-600'}`} />
              }
              <h2 className={`text-xl font-bold truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                {envolvido_encontrado?.nome || envolvido?.nome || 'Envolvido'}
              </h2>
            </div>
            <div className="flex items-center gap-3 ml-7">
              {envolvido?.cpf_cnpj && (
                <span className={`text-sm font-mono ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                  {envolvido.cpf_cnpj}
                </span>
              )}
              {envolvido_encontrado?.tipo_pessoa && (
                <span className={`text-xs px-2 py-0.5 rounded ${
                  darkMode ? 'bg-neutral-700 text-neutral-300' : 'bg-gray-200 text-gray-600'
                }`}>
                  {envolvido_encontrado.tipo_pessoa === 'JURIDICA' ? 'Jurídica' : 'Física'}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ml-4 flex-shrink-0 ${
              darkMode ? 'hover:bg-neutral-700 text-neutral-400' : 'hover:bg-gray-100 text-gray-600'
            }`}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className={`w-10 h-10 animate-spin ${darkMode ? 'text-primary-400' : 'text-primary-600'}`} />
              <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                Buscando processos...
              </p>
            </div>
          )}

          {error && !loading && (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <AlertCircle className="w-10 h-10 text-red-400" />
              <p className={`text-sm font-medium ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
                Erro ao buscar dados
              </p>
              <p className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-500'}`}>{error}</p>
            </div>
          )}

          {!loading && !error && dados && (
            <div className="p-6 space-y-5">
              {/* Resumo */}
              {envolvido_encontrado && (
                <div className={`p-4 rounded-lg border ${darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-gray-50 border-gray-200'}`}>
                  <p className={`text-xs font-semibold uppercase mb-3 ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                    Resumo
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <div>
                      <p className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>Total de processos</p>
                      <p className={`text-2xl font-bold ${darkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                        {envolvido_encontrado.quantidade_processos ?? items.length}
                      </p>
                    </div>
                    {envolvido_encontrado.outros_nomes?.length > 0 && (
                      <div>
                        <p className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'}`}>Outros nomes</p>
                        <p className={`text-sm ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                          {envolvido_encontrado.outros_nomes.join(', ')}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Lista de processos */}
              {items.length > 0 && (
                <div>
                  <p className={`text-sm font-semibold mb-3 ${darkMode ? 'text-neutral-300' : 'text-gray-700'}`}>
                    Processos encontrados ({items.length})
                  </p>
                  <div className={`space-y-3 max-h-96 overflow-y-auto pr-1`}>
                    {items.map((item, idx) => {
                      const fonte = item.fontes?.find(f => f.tipo === 'TRIBUNAL') || item.fontes?.[0];
                      const capa = fonte?.capa;
                      const isArquivado = item.fontes_tribunais_estao_arquivadas;
                      const tribunal = item.unidade_origem?.tribunal_sigla || fonte?.sigla || '-';

                      return (
                        <div key={idx} className={`p-4 rounded-lg border ${
                          darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-gray-200'
                        }`}>
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <p className={`text-sm font-bold font-mono ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                              {item.numero_cnj}
                            </p>
                            <div className="flex gap-1 flex-shrink-0">
                              {tribunal !== '-' && (
                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                  darkMode ? 'bg-primary-900/30 text-primary-400' : 'bg-primary-100 text-primary-700'
                                }`}>
                                  {tribunal}
                                </span>
                              )}
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                isArquivado
                                  ? darkMode ? 'bg-gray-800 text-gray-400' : 'bg-gray-100 text-gray-500'
                                  : darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                              }`}>
                                {isArquivado ? 'Arquivado' : 'Ativo'}
                              </span>
                            </div>
                          </div>

                          {(item.titulo_polo_ativo || item.titulo_polo_passivo) && (
                            <div className={`flex flex-wrap gap-x-4 gap-y-0.5 mb-2 text-xs ${darkMode ? 'text-neutral-400' : 'text-gray-500'}`}>
                              {item.titulo_polo_ativo && <span>Ativo: <span className={darkMode ? 'text-neutral-200' : 'text-gray-700'}>{item.titulo_polo_ativo}</span></span>}
                              {item.titulo_polo_passivo && <span>Passivo: <span className={darkMode ? 'text-neutral-200' : 'text-gray-700'}>{item.titulo_polo_passivo}</span></span>}
                            </div>
                          )}

                          {capa && (
                            <p className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-gray-400'} mb-1`}>
                              {[capa.classe, capa.assunto].filter(Boolean).join(' · ')}
                            </p>
                          )}

                          <div className={`flex flex-wrap gap-x-3 text-xs ${darkMode ? 'text-neutral-600' : 'text-gray-400'}`}>
                            {item.data_inicio && <span>Início: {formatDate(item.data_inicio)}</span>}
                            {item.quantidade_movimentacoes > 0 && <span>{item.quantidade_movimentacoes} movs.</span>}
                            {capa?.valor_causa?.valor_formatado && (
                              <span className={darkMode ? 'text-green-500' : 'text-green-600'}>{capa.valor_causa.valor_formatado}</span>
                            )}
                          </div>

                          {fonte?.url && (
                            <a
                              href={fonte.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`inline-flex items-center gap-1 mt-2 text-xs ${
                                darkMode ? 'text-primary-400 hover:text-primary-300' : 'text-primary-600 hover:text-primary-800'
                              }`}
                            >
                              <ExternalLink className="w-3 h-3" /> Ver no tribunal
                            </a>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {items.length === 0 && (
                <div className="text-center py-12">
                  <FileText className={`w-12 h-12 mx-auto mb-3 ${darkMode ? 'text-neutral-600' : 'text-gray-300'}`} />
                  <p className={`text-sm ${darkMode ? 'text-neutral-400' : 'text-gray-600'}`}>
                    Nenhum processo encontrado
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`p-4 border-t flex-shrink-0 ${darkMode ? 'border-neutral-800' : 'border-gray-200'}`}>
          <button
            onClick={onClose}
            className={`w-full py-2 rounded-lg text-sm font-medium transition-colors ${
              darkMode ? 'bg-neutral-800 hover:bg-neutral-700 text-neutral-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
            }`}
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}
